import {
  BillingAccount,
  BillingAccountDeliveryMethod,
  BillingAccountStatus,
  ContactType,
} from '../../generated/api/models.js';
import { createBillingAccount } from '../fetch.js';
import {
  activeMsg,
  billingAccountExtensionNameMsg,
  costCenterMsg,
  eInvoiceMsg,
  emailInvoiceMsg,
  englishMsg,
  fieldValueTooLong,
  finnishMsg,
  paperInvoiceMsg,
  payerDetailsMsg,
  pcsMsg,
  referenceMsg,
  swedishMsg,
  t,
} from '../i18n/index.js';
import { formatSum } from './priceUtils.js';
import { BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH } from './validationUtils.js';
import type { BasicAutoCompleteItem } from '../../components/AutoComplete/AutoComplete';

export type EOperatorItem = BasicAutoCompleteItem & {
  active?: boolean;
  electronicAddress?: string;
};

import type { BillingAccountCreateFormValues } from '../../components/CreateBillingAccount/CreateBillingAccount.js';
import type {
  BillChannel,
  BillChannelOperators,
  BillingAccountHeader,
  BillingAccountsResponse,
  Contact,
  ContactHeader,
  PostPersonBillingAccountRequest,
} from '../../generated/api/models.js';
import type { CLDropdownItem } from '../types/componentLibrary';

export enum BillChannelType {
  PAPER = 'paper',
  EMAIL = 'email',
  NETWORK = 'network',
}
export type UpsertPersonBillingAccountRequest = Omit<
  PostPersonBillingAccountRequest,
  'contactId' | 'personalIdentityCode'
>;

export enum BillingAccountSourceSystem {
  SFDC = 'SFDC',
  MIPA = 'MIPA',
  TELLUS = 'TELLUS',
  ORIGO = 'ORIGO',
}

export type ReceiverType = 'SAME_AS_CONTACT' | 'OTHER';

export const ReceiverTypes = {
  SAME_AS_CONTACT: 'SAME_AS_CONTACT' as ReceiverType,
  OTHER: 'OTHER' as ReceiverType,
};

export interface BaInvoiceType {
  displayValue: () => string;
  value: string;
}

export const billingAccountInvoiceTypes: BaInvoiceType[] = [
  {
    displayValue: () => t.U5N7('Consolidated invoice with itemised details'),
    value: BillingAccount.BillFormatTypeEnum.CONSOLIDATED_INVOICE_WITH_ITEMIZATION,
  },
  {
    displayValue: () => t.OWUZ('Consolidated invoice without itemised details'),
    value: BillingAccount.BillFormatTypeEnum.CONSOLIDATED_INVOICE_WITHOUT_ITEMIZATION,
  },
  {
    displayValue: () => t.D9PT('Cost center invoice'),
    value: BillingAccount.BillFormatTypeEnum.COST_CENTRE_INVOICE,
  },
  {
    displayValue: () => t.UEMQ('Cost center invoice with itemised details'),
    value: BillingAccount.BillFormatTypeEnum.COST_CENTRE_INVOICE_WITH_ITEMIZATION,
  },
];

// FIXME why isn't this part of BillingAccountSourceSystem, if it's a possible value for it?
export const ConsumerBA = 'Consumer BA-SFDC';

export const CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE = 'ADD_NEW';

export const isBillingAccountInSfdc = (billingAccount: BillingAccount | BillingAccountHeader): boolean => {
  return billingAccount.sourceSystem === BillingAccountSourceSystem.SFDC;
};

export const isBillingAccountInOrigo = (billingAccount: BillingAccount | BillingAccountHeader): boolean => {
  return billingAccount.sourceSystem?.toUpperCase() === BillingAccountSourceSystem.ORIGO;
};

export const isBillingAccountCancelled = (billingAccount: BillingAccount): boolean => {
  return (
    !!billingAccount.billingAccountStatus &&
    [BillingAccountStatus.CANCELLED, BillingAccountStatus.ARCHIVED].includes(billingAccount.billingAccountStatus)
  );
};

export const getFilteredBillingAccounts = (
  billingAccountState?: BillingAccountsResponse
): Array<BillingAccountHeader> | undefined => {
  return billingAccountState?.searchResults?.map(searchResult => searchResult.result)?.filter(isBillingAccountInSfdc);
};

export const getDefaultBillingAccountId = (
  billingAccounts: Array<BillingAccount | BillingAccountHeader> | undefined
) => {
  if (!billingAccounts || billingAccounts.length === 0) {
    return CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE;
  }
  return billingAccounts.reduce(
    (acc: BillingAccount | BillingAccountHeader, val: BillingAccount | BillingAccountHeader) => {
      if (!acc) {
        return val;
      }
      // Use the billing account with the latest modified is last as default
      return acc.lastModified! > val.lastModified! ? acc : val;
    }
  ).billingAccountId!;
};

export const filterAndGetDefaultBillingAccountId = (billingAccountState?: BillingAccountsResponse): string => {
  return getDefaultBillingAccountId(getFilteredBillingAccounts(billingAccountState));
};

export const getDefaultDeliveryMethod = (
  billingAccounts?: Array<BillingAccount | BillingAccountHeader>
): BillingAccountDeliveryMethod | undefined => {
  if (!billingAccounts) {
    return undefined;
  }

  const deliveryMethods = new Set(billingAccounts.map(ba => ba.deliveryMethod));

  if (deliveryMethods.size === 1) {
    return deliveryMethods.values().next().value;
  }

  return undefined;
};

export const getDefaultBillingContactId = (
  billingAccounts?: Array<BillingAccount | BillingAccountHeader>
): string | undefined => {
  if (!billingAccounts) {
    return undefined;
  }

  const billingContactIds = new Set(billingAccounts.map(ba => ba.billingContactId));

  if (billingContactIds.size === 1) {
    return billingContactIds.values().next().value;
  }

  return undefined;
};

export const billingAccountNonVisibleFields = [
  {
    name: 'customerReference1',
    getTitle: () => `${t.LVRN(referenceMsg)} 1`,
  },
  {
    name: 'customerReference2',
    getTitle: () => `${t.LVRN(referenceMsg)} 2`,
  },
  {
    name: 'customerReference3',
    getTitle: () => `${t.LVRN(referenceMsg)} 3`,
  },
  {
    name: 'customerReference4',
    getTitle: () => `${t.LVRN(referenceMsg)} 4`,
  },
  {
    name: 'billingAccountExtensionName',
    getTitle: () => t.KUTS(billingAccountExtensionNameMsg),
  },
  {
    name: 'payerNameExtension',
    getTitle: () => t.YLAI(payerDetailsMsg),
  },
  {
    name: 'billingAccountSubscriptionsCostCenter',
    getTitle: () => t.L2OG(costCenterMsg),
  },
];

export const getContactDisplayValue = (contact?: Contact | ContactHeader): string => {
  const contactSDFC = contact as Contact;
  const contactHeader = contact as ContactHeader;
  if (contactSDFC?.person && contactSDFC.contactType === ContactType.PERSON) {
    const { firstName, lastName } = contactSDFC.person;
    return `${firstName || ''} ${lastName || ''}`;
  } else if (contactSDFC?.commonFunction) {
    return contactSDFC?.commonFunction?.name || '';
  } else if (contactHeader) {
    return `${contactHeader?.firstName || ''} ${contactHeader?.lastName || ''}`;
  } else {
    return '';
  }
};

export const findContact = (billingAccount: BillingAccount, contacts: Contact[] = []) => {
  return contacts.find(({ contactId }) => contactId === billingAccount.billingContactId);
};

export const getDeliveryMethod = (method: string): string => {
  switch (method) {
    case BillingAccountDeliveryMethod.ELECTRONIC.toString():
      return t.IK1D(eInvoiceMsg);
    case BillingAccountDeliveryMethod.EMAIL.toString():
      return t.NNV9(emailInvoiceMsg);
    case BillingAccountDeliveryMethod.PAPER.toString():
      return t.TER4(paperInvoiceMsg);
    default:
      return t.C6WY('Other');
  }
};

export const getElectronicInvoiceOperatorOptions = (billChannels?: BillChannel[]) => {
  if (billChannels) {
    const networkBillChannel = billChannels.find(billChannel => billChannel.type === 'network');
    if (networkBillChannel && networkBillChannel.operators) {
      return networkBillChannel.operators.map(operator => ({
        displayValue: `${operator.name} (${operator.id})`,
        value: operator.id,
      }));
    }
  }
  return [];
};

export const getElectronicInvoiceOperatorDisplayValue = (
  electronicInvoiceOperatorValue: string,
  billChannels?: BillChannel[]
): string => {
  return (
    getElectronicInvoiceOperatorOptions(billChannels)?.find(b => b.value === electronicInvoiceOperatorValue)
      ?.displayValue ?? ''
  );
};

export const getDeliveryMethodDisplayValue = (deliveryMethod: BillingAccountDeliveryMethod) => {
  switch (deliveryMethod) {
    case BillingAccountDeliveryMethod.EMAIL:
      return t.NNV9(emailInvoiceMsg);
    case BillingAccountDeliveryMethod.PAPER:
      return t.TER4(paperInvoiceMsg);
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return t.IK1D(eInvoiceMsg);
    default:
      return BillingAccountDeliveryMethod.UNKNOWN.toString();
  }
};

export const getReceiverType = (billingAccount: BillingAccountCreateFormValues | BillingAccount | undefined) =>
  !billingAccount?.billReceiverId || billingAccount?.billReceiverId === billingAccount?.billingContactId
    ? ReceiverTypes.SAME_AS_CONTACT
    : ReceiverTypes.OTHER;

export const prepareBillingAccountSave = (
  billingAccount: BillingAccount,
  values: Partial<BillingAccount>,
  receiverType: ReceiverType
) => {
  const updatedBillingAccount = { ...billingAccount, ...values };

  if (updatedBillingAccount.deliveryMethod !== BillingAccountDeliveryMethod.EMAIL) {
    // Delete all bill receiver fields from the payload, as they might have been left behind from previous edits
    // and aren't currently used for anything else than email
    delete updatedBillingAccount.billReceiverId;
    delete updatedBillingAccount.billReceiverType;
    delete updatedBillingAccount.billReceiverName;
  } else if (updatedBillingAccount.billReceiverType === undefined) {
    // This means that the default value for EMAIL is used which is PERSON but the as no clicking has been done, the
    // object has not been updated.
    updatedBillingAccount.billReceiverType = ContactType.PERSON;
  }

  if (receiverType === ReceiverTypes.SAME_AS_CONTACT) {
    // Set the name and id back to the same as the billingContact
    updatedBillingAccount.billReceiverId = updatedBillingAccount.billingContactId;
    updatedBillingAccount.billReceiverName = updatedBillingAccount.billingContactName;
  }

  return updatedBillingAccount;
};

export const getPaperBillPrice = (billChannels?: BillChannel[]): string | undefined => {
  if (billChannels) {
    const paperBillChannel = billChannels.find(billChannel => billChannel.type === BillChannelType.PAPER);
    if (paperBillChannel) {
      return `${formatSum(paperBillChannel.price)}/${t.B3MG(pcsMsg)}`;
    }
  }
  return undefined;
};

export const getPaperBillDeliveryMethodCustomType = (
  billingAccount: BillingAccount,
  paperBillPrice?: string
): string | undefined => {
  const paperPrice = paperBillPrice || '?';
  const directPaymentPrice = formatSum(billingAccount.billPrice) || paperPrice;
  const directPaymentPaperInvoiceCustomType = `${t.NY9Q(
    'Direct payment + paper invoice'
  )} (${directPaymentPrice}/${t.B3MG('invoice')})`;

  const paperInvoiceCustomType = `${t.TER4(paperInvoiceMsg)} (${formatSum(billingAccount.billPrice)}/${t.B3MG(
    'invoice'
  )})`;

  if (billingAccount.deliveryMethodCustomType === 'DIRECT') {
    return directPaymentPaperInvoiceCustomType;
  } else if (billingAccount.billPrice) {
    return paperInvoiceCustomType;
  }
  return undefined;
};

export const getDeliveryMethodDropDownOptions = (
  billingAccount: BillingAccount,
  billChannels?: BillChannel[]
): CLDropdownItem[] => {
  const paperBillPrice = getPaperBillPrice(billChannels);
  const paperBillDisplayValue =
    getPaperBillDeliveryMethodCustomType(billingAccount, paperBillPrice) ||
    `${t.TER4(paperInvoiceMsg)} (${paperBillPrice})`;

  return [
    {
      value: BillingAccountDeliveryMethod.EMAIL,
      label: t.NNV9(emailInvoiceMsg),
    },
    {
      value: BillingAccountDeliveryMethod.PAPER,
      label: paperBillDisplayValue,
    },
    {
      value: BillingAccountDeliveryMethod.ELECTRONIC,
      label: t.IK1D(eInvoiceMsg),
    },
  ];
};

export const getDefaultElectronicOperator = (billChannels?: BillChannel[]): BillChannelOperators | undefined => {
  if (billChannels) {
    const networkBillChannel = billChannels.find(billChannel => billChannel.type === BillChannelType.NETWORK);
    if (networkBillChannel && networkBillChannel.operators && networkBillChannel.operators.length > 0) {
      const activeOperators = networkBillChannel.operators.filter(i => i.active);
      // We only want to return a default operator if there is exactly one active operator
      return activeOperators.length === 1 ? activeOperators[0] : undefined;
    }
  }
  return undefined;
};

export const getLanguageOptions = () => {
  return [
    { label: t.EXXX(finnishMsg), value: 'FI' },
    { label: t.CYY2(swedishMsg), value: 'SV' },
    { label: t.R1PB(englishMsg), value: 'EN' },
  ];
};

export const getElectronicInvoiceOperatorDropDownOptions = (billChannels?: BillChannel[]): EOperatorItem[] => {
  const networkBillChannel = billChannels?.find(billChannel => billChannel.type === BillChannelType.NETWORK);
  return (
    networkBillChannel?.operators?.map(operator => {
      const displayLabel = `${operator.name} (${operator.id})`;
      return {
        value: operator.id,
        label: displayLabel,
        displayValue: displayLabel,
        active: operator.active,
        electronicAddress: operator.electronicAddress,
        html: (
          <>
            <div>{displayLabel}</div>
            {operator.active && <div className="ds-font-size--small ds-inputhelp">{t.SFRK(activeMsg)}</div>}
          </>
        ),
      };
    }) || []
  );
};

export const getAutoPopulatedElectronicAddress = (operatorId: string, billChannels?: BillChannel[]): string => {
  const networkBillChannel = billChannels?.find(billChannel => billChannel.type === BillChannelType.NETWORK);
  const operator = networkBillChannel?.operators?.find(op => op.id === operatorId);

  if (operator?.active && operator?.electronicAddress) {
    return operator.electronicAddress;
  }

  return '';
};

export const getEmptyBillingAccount = (payerName: string) => ({
  billFormatType: BillingAccount.BillFormatTypeEnum.CONSOLIDATED_INVOICE_WITH_ITEMIZATION,
  billLanguage: 'FI',
  billingContactId: '',
  deliveryMethod: BillingAccountDeliveryMethod.ELECTRONIC,
  payerAddress: {
    countryCode: 'FIN',
    line1: '',
    postOffice: '',
    postalCode: '',
  },
  payerName,
  billingContactPhone: '',
  billElectronicAddress: '',
});

interface CreateBillingAccountArgs {
  newBillingAccount: BillingAccount;
  billReceiverSelection: ReceiverType;
  contacts?: Contact[];
  mdmId?: string;
}

export const createNewBillingAccount = async ({
  newBillingAccount,
  billReceiverSelection,
  contacts,
  mdmId,
}: CreateBillingAccountArgs) => {
  const effectiveContact = contacts?.find(c => c.contactId === newBillingAccount.billingContactId);
  if (!effectiveContact) {
    throw new Error(t.WIMV('Selected contact is not valid, please check the selection.'));
  }
  newBillingAccount.billingContactId = effectiveContact?.contactId;
  if (newBillingAccount.deliveryMethod === BillingAccountDeliveryMethod.EMAIL) {
    if (billReceiverSelection === ReceiverTypes.SAME_AS_CONTACT) {
      newBillingAccount.billReceiverId = effectiveContact?.contactId;
    }
  } else {
    delete newBillingAccount.billReceiverId;
    delete newBillingAccount.billReceiverType;
  }

  try {
    return await createBillingAccount(newBillingAccount, mdmId);
  } catch (err) {
    throw new Error(t.EBJW('Adding a new billing account failed. Please try again later.'));
  }
};

export const validateBillingAccountReferenceMaxLength = (value: string) => {
  if (value && value.length > BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH) {
    return t.XOYE(fieldValueTooLong, `${BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}`);
  }
  return undefined;
};
